export type QualityTier = 'low' | 'medium' | 'high';

export interface QualityProfile {
  readonly tier: QualityTier;
  readonly maxPixelRatio: number;
  readonly shadowMapSize: number;
  readonly shadows: boolean;
  readonly smallDetailShadows: boolean;
  readonly sceneryDensity: number;
  readonly radialSegments: number;
  readonly railRadialSegments: number;
  readonly supportRadialSegments: number;
  readonly productionHardwareLimit: number;
}

export const QUALITY_PROFILES: Readonly<Record<QualityTier, QualityProfile>> = Object.freeze({
  low: Object.freeze({
    tier: 'low',
    maxPixelRatio: 1,
    shadowMapSize: 512,
    shadows: false,
    smallDetailShadows: false,
    sceneryDensity: 0.48,
    radialSegments: 5,
    railRadialSegments: 6,
    supportRadialSegments: 6,
    productionHardwareLimit: 180,
  }),
  medium: Object.freeze({
    tier: 'medium',
    // Match the Phase 0 mobile proof: retain a 2x drawing buffer and 1024px
    // shadows while scaling scenery density before sacrificing edge quality.
    maxPixelRatio: 2,
    shadowMapSize: 1024,
    shadows: true,
    smallDetailShadows: false,
    sceneryDensity: 0.72,
    radialSegments: 8,
    railRadialSegments: 8,
    supportRadialSegments: 8,
    productionHardwareLimit: 420,
  }),
  high: Object.freeze({
    tier: 'high',
    maxPixelRatio: 2,
    shadowMapSize: 2048,
    shadows: true,
    smallDetailShadows: true,
    sceneryDensity: 1,
    radialSegments: 8,
    railRadialSegments: 10,
    supportRadialSegments: 10,
    productionHardwareLimit: 900,
  }),
});

export function getQualityProfile(tier: QualityTier): QualityProfile {
  return QUALITY_PROFILES[tier];
}

export function clampPixelRatio(devicePixelRatio: number, profile: QualityProfile): number {
  if (!Number.isFinite(devicePixelRatio) || devicePixelRatio <= 0) return 1;
  return Math.min(devicePixelRatio, profile.maxPixelRatio);
}

export function recommendQualityTier(input: {
  readonly mobile: boolean;
  readonly reducedMotion?: boolean;
  readonly deviceMemoryGb?: number;
}): QualityTier {
  // Reduced motion is an accessibility preference, not a GPU capability
  // signal. Treat capable mobile devices as medium so they keep MSAA and
  // shadows; reserve the visually reduced tier for known low-memory devices.
  if ((input.deviceMemoryGb ?? Infinity) <= 4) return 'low';
  if (input.mobile || (input.deviceMemoryGb ?? Infinity) <= 8) return 'medium';
  return 'high';
}
